/**
 * Pure helpers for the /my-family page (Plan 06).
 *
 * Display + label formatting lives here so the UI components stay
 * focused on rendering and the rules are unit-testable without
 * spinning up React. Anything that compares names, formats roles, or
 * derives "sign-in method" labels lives here.
 */

export interface NameSource {
  firstName: string | null | undefined
  lastName: string | null | undefined
  preferredName?: string | null | undefined
}

export interface SignInSource {
  email: string | null | undefined
  canSignIn: boolean
}

/**
 * Initials for the Avatar fallback. Uses preferred name when set
 * (so "Sammy" beats "Samuel"); falls back to first+last; else "?".
 */
export function getInitials(source: NameSource): string {
  const base = source.preferredName ?? `${source.firstName ?? ''} ${source.lastName ?? ''}`.trim()
  if (!base) {
    return '?'
  }
  const parts = base.split(/\s+/).filter(Boolean)
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase()
  }
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/**
 * Display name per the edge-case rule in Plan 06:
 *   - "First (Preferred)" when `preferredName` is set + differs from first
 *   - "First Last" otherwise
 *   - "Unknown" if everything is empty
 */
export function getDisplayName(source: NameSource): string {
  const first = (source.firstName ?? '').trim()
  const last = (source.lastName ?? '').trim()
  const preferred = (source.preferredName ?? '').trim()

  if (preferred && preferred !== first) {
    return last ? `${first} (${preferred}) ${last}` : `${first} (${preferred})`
  }
  const combined = `${first} ${last}`.trim()
  return combined || 'Unknown'
}

/**
 * "First L." — the compact form for narrow rows where the full name would
 * squeeze the role tag or the email beside it.
 *
 * Uses the preferred name as the given name when set, matching `getInitials`
 * ("Sammy W." beats "Samuel W."). Falls back to `getDisplayName` whenever there
 * is no surname to abbreviate, so a mononym or an empty record is never mangled
 * into a bare initial or a stray period.
 */
export function getShortDisplayName(source: NameSource): string {
  const first = (source.preferredName ?? '').trim() || (source.firstName ?? '').trim()
  const last = (source.lastName ?? '').trim()
  if (!first || !last) {
    return getDisplayName(source)
  }
  // Taking just the first character also normalizes a surname that is already an
  // initial ("W" or "W.") to a single "W." rather than "W.." .
  return `${first} ${last[0]!.toUpperCase()}.`
}

/**
 * Short label of someone's sign-in posture. Drives the secondary
 * line of each row in the People-on-account section.
 *
 *   - `email`, when set -> show the email
 *   - `canSignIn = true` but no email -> "Sign-in account"
 *   - else -> "No sign-in account"
 */
export function getSignInLabel(source: SignInSource): string {
  if (source.email) {
    return source.email
  }
  if (source.canSignIn) {
    return 'Sign-in account'
  }
  return 'No sign-in account'
}

/**
 * Human-friendly label for an `AccountRelationship` enum value.
 */
export function formatRelationship(rel: string): string {
  if (!rel) {
    return ''
  }
  return rel.charAt(0) + rel.slice(1).toLowerCase()
}

/**
 * Two-digit month + day -> "Aug 12" for the birthday display row.
 * Returns null when either piece is missing.
 *
 * The schema currently stores `User.birthday` as a full Date — for
 * Plan 06 the page reads only month + day from that value (year is
 * not displayed). A future schema split into birthMonth/birthDay is
 * planned; until then this helper isolates the conversion so the
 * card stays unchanged.
 */
export function formatBirthdayMonthDay(birthday: string | Date | null | undefined): string | null {
  if (!birthday) {
    return null
  }
  const d = birthday instanceof Date ? birthday : new Date(birthday)
  if (Number.isNaN(d.getTime())) {
    return null
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`
}

/**
 * Pair of (birthMonth, birthDay) -> "Aug 12". Used by the add-
 * student sheet for preview. Validation of the pair lives in the
 * sheet's submit flow.
 */
export function formatMonthDay(month: number | null | undefined, day: number | null | undefined): string | null {
  if (month == null || day == null) {
    return null
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  if (month < 1 || month > 12) {
    return null
  }
  const name = months[month - 1]
  if (!name) {
    return null
  }
  return `${name} ${day}`
}

/**
 * Validity check for a (month, day) pair using year 2000 as the
 * leap-year reference (so Feb 29 is valid; Feb 30 is not).
 */
export function isValidMonthDay(month: number | null | undefined, day: number | null | undefined): boolean {
  if (month == null && day == null) {
    return true
  }
  if (month == null || day == null) {
    return false
  }
  if (!Number.isInteger(month) || !Number.isInteger(day)) {
    return false
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false
  }
  const d = new Date(2000, month - 1, day)
  return d.getMonth() === month - 1 && d.getDate() === day
}

/**
 * Light email-syntax check. Used by the add-parent / sign-in
 * branches; we don't want a heavy validator on the wire. The
 * server enforces strict validation via Zod when the upstream
 * mutation lands.
 */
export function isPlausibleEmail(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) {
    return false
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

/**
 * Light phone validation matching the server-side regex used for
 * adult-invite flows in Plan 36. Permissive: digits, spaces, dashes,
 * parens, optional leading +; 6–20 chars.
 */
export function isPlausiblePhone(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) {
    return false
  }
  return /^\+?[0-9\-\s()]{6,20}$/.test(trimmed)
}

/**
 * Presentation state for a pending co-owner invite row on the
 * co-owner management card. Derives the badge label + secondary line
 * from the Invitation's delivery status and expiry.
 */
export interface PendingInviteStatusMeta {
  /** Badge text — "Invited", "Expired", or "Delivery failed". */
  label: string
  /** Whether the invite can no longer be claimed as-is (drives badge tone). */
  isProblem: boolean
  /** Secondary line under the email. */
  detail: string
}

export function getPendingInviteStatusMeta(
  invite: { status: string; expiresAt: string },
  now: Date = new Date(),
): PendingInviteStatusMeta {
  const expires = new Date(invite.expiresAt)
  const expired =
    invite.status === 'EXPIRED' || (!Number.isNaN(expires.getTime()) && expires.getTime() <= now.getTime())
  if (expired) {
    return { label: 'Expired', isProblem: true, detail: 'Invite expired — resend to issue a new link.' }
  }
  if (invite.status === 'BOUNCED' || invite.status === 'COMPLAINED' || invite.status === 'FAILED') {
    return { label: 'Delivery failed', isProblem: true, detail: "The invite email couldn't be delivered." }
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const expiryLabel = Number.isNaN(expires.getTime()) ? null : `${months[expires.getMonth()]} ${expires.getDate()}`
  return {
    label: 'Invited',
    isProblem: false,
    detail: expiryLabel ? `Invitation sent - expires ${expiryLabel}.` : 'Invitation sent.',
  }
}

/**
 * Success-toast description after claiming enrolled students with a
 * school activation code. Singular vs plural on the count.
 */
export function formatClaimedStudentsMessage(count: number): string {
  return count === 1 ? '1 student loaded.' : `${count} students loaded.`
}

/** The member fields `listHouseholdStudents` reads (a slice of `MyFamilyMember`). */
export interface HouseholdMemberSource {
  relationship: string
  isArchived: boolean
  user: { id: string } | null
}

/**
 * The students /my-family lists: live members across the viewer's
 * households, deduped by user, minus the adults — the viewer, and any staff
 * self-diner (a teacher ordering their own lunch, `relationship: OTHER`,
 * written only by `POST /api/family/self`).
 *
 * The self-diner is excluded by its own row, not by "is this me": View-as
 * signs in as a synthetic household-view identity, so a viewer-only check
 * listed the teacher as a "Student" there (PROD-1439), and would do the same
 * for a co-parent of a teacher's household.
 */
export function listHouseholdStudents<M extends HouseholdMemberSource>(
  households: ReadonlyArray<{ members: ReadonlyArray<M> }>,
  viewerUserId: string | null | undefined,
): M[] {
  const seen = new Set<string>()
  const students: M[] = []
  for (const member of households.flatMap((household) => household.members)) {
    const userId = member.user?.id
    if (
      member.isArchived ||
      !userId ||
      userId === viewerUserId ||
      member.relationship === 'OTHER' ||
      seen.has(userId)
    ) {
      continue
    }
    seen.add(userId)
    students.push(member)
  }
  return students
}
